<template>
  <div class="campo-textarea">
    <b-form-group :state="state" :invalid-feedback="erroExibido">
      <template #label>
        <span
          class="campo-textarea__rotulo"
          :class="{ 'campo-textarea__rotulo--obrigatorio': obrigatorio }"
        >
          {{ rotulo }}
        </span>

        <span v-if="dica" class="campo-textarea__dica">
          {{ dica }}
        </span>
      </template>

      <b-form-textarea
        :value="valorExibicao"
        :rows="linhas"
        :state="state"
        :disabled="modoVisualizacao || desabilitado"
        :placeholder="placeholder"
        :maxlength="maxlength"
        @input="
          modoVisualizacao || desabilitado ? null : $emit('input', $event)
        "
      />

      <contador-caracteres
        v-if="exibirContador || mostrarAvisoLimite"
        :atual="tamanhoAtual"
        :limite="maxlength"
        :minimo="minimo"
        :atual-minimo="tamanhoParaMinimo"
        :invalido="Boolean(erro) || abaixoDoMinimo"
        :desabilitado="modoVisualizacao || desabilitado"
        :mostrar-aviso-limite="mostrarAvisoLimite"
        :exibir-contagem="exibirContador"
      />
    </b-form-group>
  </div>
</template>

<script lang="js">
import ContadorCaracteres from "./ContadorCaracteres.vue";
import { isPlaceholderVazio } from "../../../domain/rules/valores.js";

/**
 * Campo de área de texto reutilizável do cadastro de relato.
 *
 * @prop {string} rotulo - texto do label (adiciona "*" se obrigatorio)
 * @prop {string} value - valor do campo
 * @prop {number} linhas - quantidade de linhas visíveis (default 3)
 * @prop {string} dica - texto de ajuda exibido junto ao label
 * @prop {boolean} obrigatorio - marca o campo como obrigatório
 * @prop {boolean} desabilitado - desabilita o campo
 * @prop {string} erro - mensagem de erro; preenche = campo inválido
 * @prop {number|string|null} maxlength - limite máximo de caracteres do campo
 * @prop {number|string} minimo - quantidade mínima para exibir orientação de preenchimento
 * @prop {boolean} exibirContador - exibe a contagem de caracteres em badge
 * @prop {boolean} exibirAvisoLimite - exibe aviso ao alcançar o limite (default true)
 *
 * @emits input {string} novo valor
 */
export default {
  name: "CampoTextarea",

  components: {
    ContadorCaracteres
  },

  inject: {
    modoVisualizacao: {
      type: Boolean,
      default: false
    }
  },

  props: {
    rotulo: {
      type: String,
      required: true
    },

    placeholder: {
      type: String,
      default: "Descricao..."
    },

    value: {
      type: String,
      default: ""
    },

    linhas: {
      type: Number,
      default: 3
    },

    dica: {
      type: String,
      default: ""
    },

    obrigatorio: {
      type: Boolean,
      default: false
    },

    desabilitado: {
      type: Boolean,
      default: false
    },

    erro: {
      type: String,
      default: ""
    },

    maxlength: {
      type: [Number, String],
      default: null
    },

    minimo: {
      type: [Number, String],
      default: 0
    },

    exibirContador: {
      type: Boolean,
      default: false
    },

    exibirAvisoLimite: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    state() {
      return this.erro || this.abaixoDoMinimo ? false : null;
    },
    erroExibido() {
      return this.abaixoDoMinimo ? "" : this.erro;
    },
    valorExibicao() {
      if (!this.modoVisualizacao) return this.value;
      return isPlaceholderVazio(this.value) ? "-" : this.value;
    },
    tamanhoAtual() {
      return String(this.value ?? "").length;
    },
    tamanhoParaMinimo() {
      return String(this.value ?? "").trim().length;
    },
    abaixoDoMinimo() {
      const minimo = Number(this.minimo);
      return (
        Number.isFinite(minimo) &&
        minimo > 0 &&
        this.tamanhoParaMinimo > 0 &&
        this.tamanhoParaMinimo < minimo &&
        !this.modoVisualizacao &&
        !this.desabilitado
      );
    },
    limiteAtingido() {
      const limite = Number(this.maxlength);
      return Number.isFinite(limite) && limite > 0 && this.tamanhoAtual >= limite;
    },
    mostrarAvisoLimite() {
      return (
        this.exibirAvisoLimite &&
        this.limiteAtingido &&
        !this.erro &&
        !this.modoVisualizacao &&
        !this.desabilitado
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/_main";
@import "../../../styles/components/_campos";
</style>


